'use client';

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useAIShortcuts } from '@/hooks/useAIShortcuts';
import Tooltip from '@/components/common/Tooltip';

interface InlineAiPromptProps {
  editor: any; // The editor instance
  onCommand: (prompt: string) => Promise<void>;
  onClose: () => void;
  // Chat integration props
  sendMessage?: (message: string) => Promise<boolean>;
}

const InlineAiPrompt: React.FC<InlineAiPromptProps> = ({ editor, onCommand, onClose, sendMessage }) => {

  const [input, setInput] = useState('');
  const [position, setPosition] = useState<React.CSSProperties>({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const savedSelection = useRef<{ from: number; to: number } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { shortcuts } = useAIShortcuts();

  const handleClose = useCallback((restoreSelection = true) => {
    if (restoreSelection && savedSelection.current) {
      // Restore focus and selection to the editor
      const { from, to } = savedSelection.current;
      editor.commands.focus();
      editor.commands.setTextSelection({ from, to });
    }
    onClose();
  }, [editor, onClose]);

  useEffect(() => {
    // Save the current selection
    const { from, to } = editor.state.selection;
    savedSelection.current = { from, to };

    // Calculate position relative to the editor container
    try {
      const coords = editor.view.coordsAtPos(from);
      const editorRect = editor.view.dom.getBoundingClientRect();

      const promptWidth = 320; // min-w-[320px]
      const spaceRight = editorRect.right - coords.left;

      if (spaceRight < promptWidth) {
        // Not enough space on the right, align to the right edge
        setPosition({
          right: 0,
          top: coords.top - editorRect.top + 24,
        });
      } else {
        // Enough space, align to the left of the cursor
        setPosition({
          left: coords.left - editorRect.left,
          top: coords.top - editorRect.top + 24, // Add some offset below the cursor
        });
      }
    } catch (error) {
      console.error('Error calculating position:', error);
      // Fallback position
      setPosition({ left: 20, top: 20 });
    }

    // Focus the input field when the component mounts
    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);

    // Add event listener for the Escape key to close the prompt
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleClose(true); // Restore selection on escape
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [editor, handleClose]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        handleClose(false); // Do not restore selection on click outside
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClose]);

  // Auto-resize textarea height
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
      const scrollHeight = inputRef.current.scrollHeight;
      inputRef.current.style.height = `${scrollHeight}px`;
    }
  }, [input]);

  const handleSubmit = async (event?: React.FormEvent) => {
    event?.preventDefault();
    console.log('Form submitted with prompt:', input);

    if (input.trim() && !isLoading) {
      executeCommand(input.trim());
    }
  };

  const executeCommand = async (commandText: string) => {
    setIsLoading(true);
    setError(null);

    try {
      // If sendMessage is available, use it to integrate with chat panel
      if (sendMessage) {
        console.log('Using chat panel integration');
        const success = await sendMessage(commandText);
        if (success) {
          // Success - restore focus and close
          if (savedSelection.current) {
            const { from, to } = savedSelection.current;
            setTimeout(() => {
              editor.commands.focus();
              editor.commands.setTextSelection({ from, to });
            }, 0);
          }
          onClose();
        } else {
          // sendMessage handles error state internally, but we still need to handle UI
          setError('Content Copilot request failed. Please try again.');
          setIsLoading(false);
          setTimeout(() => {
            inputRef.current?.focus();
          }, 0);
        }
      } else {
        // Fallback to direct command execution
        console.log('Using direct command execution');
        await onCommand(commandText);

        // Success - restore focus and close
        if (savedSelection.current) {
          const { from, to } = savedSelection.current;
          setTimeout(() => {
            editor.commands.focus();
            editor.commands.setTextSelection({ from, to });
          }, 0);
        }
        onClose();
      }
    } catch (error) {
      console.error('AI command failed:', error);
      setError(error instanceof Error ? error.message : 'Content Copilot request failed. Please try again.');
      setIsLoading(false);
      // Keep the prompt open for retry
      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
    }
  };

  const handleQuickAction = async (prompt: string) => {
    setInput(prompt);
    await executeCommand(prompt);
  };

  const handleRetry = () => {
    setError(null);
    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  };

  return (
    <div
      ref={containerRef}
      className="absolute z-50"
      style={position}
    >
      <div 
        role="dialog"
        className="bg-white border border-gray-300 rounded-md shadow-lg min-w-[320px] flex flex-col"
      >
        <form onSubmit={handleSubmit} className="flex items-center p-2">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Enter Content Copilot command (Shift+Enter for newline)..."
            className="flex-grow p-2 border-0 focus:outline-none focus:ring-0 text-sm bg-transparent resize-none overflow-y-hidden text-gray-900"
            disabled={isLoading}
            rows={1}
            onKeyDown={(e) => {
              if (e.key === 'Escape' && !isLoading) {
                handleClose();
              }
              if (e.key === 'Enter' && !e.shiftKey && !isLoading) {
                e.preventDefault();
                handleSubmit(e as any);
              }
            }}
          />
          <button
            type="submit"
            className="ml-2 px-3 py-1 bg-blue-500 text-white rounded-md text-sm hover:bg-blue-600 disabled:opacity-50 flex items-center gap-1"
            disabled={!input.trim() || isLoading}
          >
            {isLoading ? (
              <>
                <svg className="animate-spin h-3 w-3 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Processing...
              </>
            ) : (
              'Submit'
            )}
          </button>
        </form>

        {/* Quick Actions Chips */}
        {!isLoading && shortcuts.length > 0 && (
          <div className="px-2 pb-2 flex flex-wrap gap-1.5 border-t border-gray-100 pt-2">
            {shortcuts.slice(0, 5).map(shortcut => (
              <Tooltip
                key={shortcut.id}
                content={shortcut.prompt}
                position="top"
                delay={300}
                inline={true}
              >
                <button
                  onClick={() => handleQuickAction(shortcut.prompt)}
                  className="px-2 py-1 text-[10px] bg-gray-50 hover:bg-blue-50 text-gray-600 hover:text-blue-600 border border-gray-200 hover:border-blue-200 rounded-md transition-colors text-left truncate max-w-[120px]"
                >
                  {shortcut.name}
                </button>
              </Tooltip>
            ))}
          </div>
        )}

        {error && (
          <div className="px-3 pb-3">
            <div className="bg-red-50 border border-red-200 rounded-md p-2">
              <div className="flex items-start gap-2">
                <svg className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <div className="flex-1">
                  <p className="text-sm text-red-700">{error}</p>
                  <button
                    onClick={handleRetry}
                    className="mt-1 text-xs text-red-600 hover:text-red-800 underline"
                  >
                    Try again
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InlineAiPrompt;
